/**
 * Roles and storages per member (F20, docs/14-anmeldung.md#rollen-und-lager-je-mitglied): what a
 * member may do (the role) and where (the granted storages). The server enforces both when it hands
 * out and takes in data; the app only follows what the server says.
 */
import type { Entity } from "./entities.ts";
import type { InventoryReport } from "./inventory.ts";
import type { Resolution, Snapshot } from "./ledger.ts";
import type { Booking, Id } from "./model.ts";

export type Role = "owner" | "manager" | "staff" | "viewer";
export const ROLES: readonly Role[] = ["owner", "manager", "staff", "viewer"];
export const ROLE_LABELS: Record<Role, string> = { owner: "Inhaber", manager: "Verwaltung", staff: "Mitarbeit", viewer: "Nur sehen" };

/** A granted storage: book in it, or only see it. A storage without a grant the member does not see. */
export type Grant = "book" | "view";
export const GRANTS: readonly Grant[] = ["book", "view"];

export interface Access {
  role: Role;
  /** The granted storages; null: all of them (owners, and the fixed access of the spike). */
  lagers: Record<Id, Grant> | null;
}

/** Full access: owners and the fixed access of the spike. */
export const FULL_ACCESS: Access = { role: "owner", lagers: null };

/** Amounts and values only for owners and managers. */
export const seesPrices = (role: Role) => role === "owner" || role === "manager";
/** Master data (articles, storages, inventories, the card …) only for owners and managers. */
export const editsMasterData = (role: Role) => role === "owner" || role === "manager";

export const canSee = (access: Access, lager: Id) => access.lagers === null || access.lagers[lager] !== undefined;
export const canBook = (access: Access, lager: Id) => access.role !== "viewer" && (access.lagers === null || access.lagers[lager] === "book");

/** The requested storages a member may see. */
export const visibleLagers = (access: Access, lagers: Id[]) => lagers.filter((l) => canSee(access, l));

// ---- without prices: what staff and viewers get ----

export function bookingWithoutPrice(b: Booking): Booking {
  if (b.kind !== "in" || b.amount === undefined) return b;
  const { amount: _, ...rest } = b;
  return rest;
}

export function snapshotWithoutPrices(s: Snapshot): Snapshot {
  return { ...s, lots: s.lots.map((l) => ({ ...l, unitPrice: null })), averages: s.averages.map((a) => ({ ...a, price: null })) };
}

export function resolutionsWithoutPrices(r: Record<Id, Resolution>): Record<Id, Resolution> {
  return Object.fromEntries(
    Object.entries(r).map(([id, res]) => [id, { parts: res.parts.map((p) => ({ lot: p.lot && { ...p.lot, unitPrice: null }, qty: p.qty, unitPrice: null })) }]),
  );
}

/** The frozen report of an inventory carries values; quantities stay. */
export function entityWithoutPrices(e: Entity): Entity {
  const report = e.type === "inventory" ? (e.fields.report as InventoryReport | undefined) : undefined;
  if (!report || typeof report !== "object" || !Array.isArray(report.rows)) return e;
  const plain: InventoryReport = {
    ...report,
    rows: report.rows.map((r) => ({ ...r, value: 0, restValue: 0, causes: r.causes.map((c) => ({ ...c, value: 0 })) })),
    totals: { ...report.totals, value: 0, causesValue: 0, restValue: 0 },
  };
  return { ...e, fields: { ...e.fields, report: plain } };
}

/** Storages and locations outside the granted storages stay hidden, Eingänge without prices; everything else is the same for all. */
export function entityVisible(access: Access, e: Entity): boolean {
  // An Eingang carries what each line cost (docs/18-vorratsbuch.md, Eingänge).
  if (e.type === "inbound" && !seesPrices(access.role)) return false;
  if (access.lagers === null) return true;
  if (e.type === "lager") return canSee(access, e.id);
  if (e.type === "location") return typeof e.fields.lager === "string" && canSee(access, e.fields.lager);
  return true;
}
